import React, { useCallback } from 'react';
import { TypedMemo } from '../../../common/types';
import { useWatermarkChoice } from './useWatermarkChoice';
import type { WatermarkChoiceRequest } from './watermarkChoiceStore';

/** What the choice shows about the file. The link adds the surface and the address. */
export type WatermarkDownloadFile = Omit<WatermarkChoiceRequest, 'surface' | 'downloadHref' | 'onContinue'>;

interface WatermarkDownloadLinkProps {
  /** The absolute, same-origin address of the file. */
  href: string;
  hasWatermark: boolean;
  file: WatermarkDownloadFile;
  /** `zip` for the kit zip link; a file link is a `download`. */
  surface?: 'download' | 'zip';
  className?: string;
  children: React.ReactNode;
  testId?: string;
  /** Called when the download starts: at once, or from the choice. */
  onDownload?: () => void;
}

/**
 * A download link of a file (design: trial-upsell a2). With no watermark, or on a paid plan, it is
 * the plain `<a href download>`. On Free and the trial a watermarked file opens the choice on each
 * click; the second row of the choice is the same `<a href download>`.
 */
const WatermarkDownloadLinkComponent: React.FC<WatermarkDownloadLinkProps> = (props) => {
  const { href, hasWatermark, file, surface = 'download', className, children, testId, onDownload } = props;
  const { gate } = useWatermarkChoice();
  const click = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (gate(hasWatermark, { ...file, surface, downloadHref: href, onDownload })) {
        event.preventDefault();
        return;
      }
      onDownload?.();
    },
    [file, gate, hasWatermark, href, onDownload, surface]
  );
  return (
    <a className={className} href={href} download onClick={click} data-testid={testId}>
      {children}
    </a>
  );
};

export const WatermarkDownloadLink = TypedMemo(WatermarkDownloadLinkComponent);
